import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { getBookers, addBooker, updateBooker, deleteBooker } from 'services/adminService';
import Toast from 'components/ux/toast/Toast';

const Admin = () => {
  const { booker, logout } = useAuth();
  const navigate = useNavigate();

  const [bookers, setBookers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ name: '', email: '' });
  const [adding, setAdding] = useState(false);
  const [newBooker, setNewBooker] = useState({ name: '', email: '' });
  const [saving, setSaving] = useState(false);

  const fetchBookers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getBookers();
      setBookers(data.bookers);
    } catch (err) {
      if (err.status === 401) {
        logout();
        navigate('/login');
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    fetchBookers();
  }, [fetchBookers]);

  const handleEdit = (b) => {
    setEditingId(b.id);
    setEditValues({ name: b.name, email: b.email });
    setAdding(false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({ name: '', email: '' });
  };

  const handleSaveEdit = async () => {
    if (!editValues.name.trim() || !editValues.email.trim()) {
      setError('Name and email are required');
      return;
    }
    try {
      setSaving(true);
      await updateBooker(editingId, editValues);
      setEditingId(null);
      await fetchBookers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the booker list?`)) return;
    try {
      setSaving(true);
      await deleteBooker(id);
      await fetchBookers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newBooker.name.trim() || !newBooker.email.trim()) {
      setError('Name and email are required');
      return;
    }
    try {
      setSaving(true);
      await addBooker(newBooker);
      setNewBooker({ name: '', email: '' });
      setAdding(false);
      await fetchBookers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
        <p className="text-brand-muted text-center">Loading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-brand-dark">Manage Bookers</h1>
          <p className="text-sm text-brand-muted mt-1">Add, edit, or remove Nordic salespersons</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="text-sm text-brand-muted hover:text-brand-dark transition-colors"
        >
          Back to portal
        </button>
      </div>

      {error && (
        <div className="mb-4">
          <Toast message={error} type="error" dismissError={() => setError('')} />
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border bg-gray-50">
              <th className="text-left px-4 py-3 font-medium text-brand-dark">Name</th>
              <th className="text-left px-4 py-3 font-medium text-brand-dark">Email</th>
              <th className="text-left px-4 py-3 font-medium text-brand-dark hidden md:table-cell">Role</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {bookers.map((b) => (
              <tr key={b.id} className="border-b border-brand-border last:border-b-0">
                {editingId === b.id ? (
                  <>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={editValues.name}
                        onChange={(e) => setEditValues((v) => ({ ...v, name: e.target.value }))}
                        className="input-field text-sm py-1"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="email"
                        value={editValues.email}
                        onChange={(e) => setEditValues((v) => ({ ...v, email: e.target.value }))}
                        className="input-field text-sm py-1"
                      />
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell">
                      <span className="text-xs text-brand-muted">{b.isAdmin ? 'Admin' : 'Booker'}</span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="text-xs text-green-700 hover:text-green-900 mr-2"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="text-xs text-brand-muted hover:text-brand-dark"
                      >
                        Cancel
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 text-brand-dark">{b.name}</td>
                    <td className="px-4 py-3 text-brand-muted">{b.email}</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${b.isAdmin ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                        {b.isAdmin ? 'Admin' : 'Booker'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleEdit(b)}
                        className="text-xs text-brand-muted hover:text-brand-dark mr-2"
                      >
                        Edit
                      </button>
                      {b.id !== booker?.id && (
                        <button
                          onClick={() => handleDelete(b.id, b.name)}
                          disabled={saving}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}

            {adding && (
              <tr className="border-b border-brand-border bg-gray-50">
                <td className="px-4 py-2">
                  <input
                    type="text"
                    value={newBooker.name}
                    onChange={(e) => setNewBooker((v) => ({ ...v, name: e.target.value }))}
                    placeholder="Full name"
                    className="input-field text-sm py-1"
                    autoFocus
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="email"
                    value={newBooker.email}
                    onChange={(e) => setNewBooker((v) => ({ ...v, email: e.target.value }))}
                    placeholder="email@nordic.be"
                    className="input-field text-sm py-1"
                  />
                </td>
                <td className="px-4 py-2 hidden md:table-cell">
                  <span className="text-xs text-brand-muted">Booker</span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={handleAdd}
                    disabled={saving}
                    className="text-xs text-green-700 hover:text-green-900 mr-2"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setAdding(false); setNewBooker({ name: '', email: '' }); }}
                    className="text-xs text-brand-muted hover:text-brand-dark"
                  >
                    Cancel
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!adding && (
        <button
          onClick={() => { setAdding(true); setEditingId(null); }}
          className="btn-gold mt-4"
        >
          Add booker
        </button>
      )}

    </div>
  );
};

export default Admin;
